<template>
  <div
    ref="dpplTreeselect"
    class="d-flex flex-column align-start justify-start dppl-treeselect"
  >
    <div
      ref="searchInput"
      class="search-row"
    >
      <div class="search-input">
        <TreeSelectHeaderNew v-if="label" :label="label" />
        <TreeSelectSearchNew
          v-model="searchQuery"
          :selected="modelValue"
          :is-select="!flat"
          :placeholder="placeholder"
          :opened="opened"
          @delete-key-down="handleDelete"
          @focus="opened = true"
          @toggle-dropdown="opened = !opened"
        />
        <v-btn
          v-if="showInlineApply"
          color="primary"
          :rounded="8"
          elevation="0"
          @click="$emit('apply-filters')"
        >
          Apply
        </v-btn>
      </div>
      <TreeSelectSelectedOptions
        :selected="modelValue"
        :label-key="labelKey"
        :id-key="idKey"
        @remove-option="removeOption"
      />
    </div>
    <div
      v-if="flat && !hideOptions"
      ref="treeselectDropdown"
      class="actions pt-1"
    >
      <TreeselectLoadingText
        v-if="loading || (async && !currentOptions.length)"
        :is-loading="loading"
      />
      <DpplTreeViewNew
        v-else
        :options="currentOptions"
        :model-value="modelValue"
        :max-height="maxHeight"
        :show-select-all="Boolean(
          showSelectAll && searchQuery.length > 2 && currentOptions?.length,
        )"
        :all-are-selected="allAreSelected"
        @update:model-value="$emit('update:modelValue', $event)"
        @update:selected-filters="$emit('update:modelValue', $event)"
        @select-all="selectAll"
      >
        <template #option-label="{ option: slotOption }">
          <slot
            name="option-label"
            :option="slotOption"
          />
        </template>
      </DpplTreeViewNew>
    </div>
  </div>
  <div
    v-if="!flat && opened && !hideOptions"
    ref="treeselectDropdown"
    class="action "
    :class="{
      opened: !flat && opened,
    }"
  >
    <div
      class="tree-view-dropdown pt-2"
      :class="directionClass"
      :style="{ minWidth: `${width}px` }"
    >
      <TreeselectLoadingText
        v-if="loading || (async && !currentOptions.length)"
        :is-loading="loading"
      />
      <DpplTreeViewNew
        v-else
        :options="currentOptions"
        :model-value="modelValue"
        :max-height="maxHeight"
        :id-key="idKey"
        :show-select-all="Boolean(
          showSelectAll && searchQuery.length > 2 && currentOptions?.length,
        )"
        :all-are-selected="allAreSelected"
        @update:model-value="$emit('update:modelValue', $event)"
        @update:selected-filters="$emit('update:modelValue', $event)"
        @select-all="selectAll"
      >
        <template #option-label="{ option: slotOption }">
          <slot
            name="option-label"
            :option="slotOption"
          />
        </template>
      </DpplTreeViewNew>
    </div>
  </div>
</template>

<script setup>
import {
  ref, watch, computed,
} from 'vue';
import DpplTreeViewNew from '@/components/filter/treeSelectNewDesign/DpplTreeViewNew.vue';
import { flattenNestedOptions, createSearchIndex, lookupOptions } from '@/stores/filters/helpers';
import { debounce } from 'lodash-es';
import TreeSelectHeaderNew from '@/components/filter/treeSelectNewDesign/TreeSelectHeaderNew.vue';
import TreeSelectSearchNew from '@/components/filter/treeSelectNewDesign/TreeSelectSearchNew.vue';
import TreeselectLoadingText from '@/components/shared/Treeselect/TreeselectLoadingText.vue';
import TreeSelectSelectedOptions from '@/components/filter/treeSelectNewDesign/TreeSelectSelectedOptions.vue';
import { getAsyncSearchResults } from '@/api/asyncSearch';
import { useElementSize, onClickOutside } from '@vueuse/core';

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
  options: {
    type: Array,
    default: () => [],
  },
  maxHeight: {
    type: Number,
    default: 300,
  },
  placeholder: {
    type: String,
    default: 'Select',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  async: {
    type: Boolean,
    default: false,
  },
  asyncUrl: {
    type: Object,
    default: () => ({}),
  },
  label: {
    type: String,
    default: '',
  },
  showSelectAll: {
    type: Boolean,
    default: false,
  },
  showInlineApply: {
    type: Boolean,
    default: false,
  },
  flat: {
    type: Boolean,
    default: false,
  },
  attachDropdown: {
    type: String,
    default: 'body',
  },
  responseValueKey: {
    type: String,
    default: '',
  },
  labelKey: {
    type: String,
    default: 'label',
  },
  searchKeys: {
    type: Array,
    default: () => [],
  },
  idKey: {
    type: String,
    default: 'id',
  },
  hideOptions: {
    type: Boolean,
    default: false,
  },
});

const emits = defineEmits(['input', 'open', 'searchChange', 'update:modelValue', 'apply-filters', 'add-snackbar']);

// State
const dpplTreeselect = ref(null);
const searchInput = ref(null);
const treeselectDropdown = ref(null);
const searchQuery = ref('');
const currentOptions = ref([]);
const loading = ref(false);
const opened = ref(false);

const { width } = useElementSize(dpplTreeselect);

// Computed
const flattenedOptions = computed(() => flattenNestedOptions(
  props.options,
  props.searchKeys.length ? props.searchKeys : [props.labelKey],
  props.idKey,
));
const searchIndex = computed(() => createSearchIndex(
  flattenedOptions.value,
  props.searchKeys.length ? props.searchKeys : [props.labelKey],
));
const allAreSelected = computed(() => searchQuery.value.length > 2 && currentOptions.value
  .every((option) => props.modelValue
    .some(({ [props.idKey]: id }) => id === option[props.idKey])));

// If the dropdown is going to overflow off the screen we want to open it on top
const directionClass = computed(() => {
  if (opened.value && dpplTreeselect.value && treeselectDropdown.value) {
    const { bottom } = dpplTreeselect.value.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const dropdownHeight = treeselectDropdown.value.querySelector('.tree-view-dropdown').clientHeight;

    if (bottom + dropdownHeight > windowHeight) {
      return 'open-on-top';
    }
    return '';
  }
  return '';
});

// Methods
const handleAsyncSearch = async (searchQueryVal) => {
  if (searchQueryVal) {
    try {
      loading.value = true;

      const matchingOptions = await getAsyncSearchResults({
        query: searchQueryVal,
        urlObj: props.asyncUrl,
      });

      const newValue = props.responseValueKey
        ? matchingOptions[props.responseValueKey]
        : matchingOptions;

      currentOptions.value = !searchQuery.value ? [] : newValue;
    } catch (error) {
      emits('add-snackbar', { text: 'There was an error loading your search results' });

      throw error;
    } finally {
      loading.value = false;
    }
  } else {
    currentOptions.value = [];
  }
};

const handleSearch = (searchQueryVal, options) => {
  if (searchQueryVal) {
    const matchingOptions = lookupOptions(searchQueryVal, searchIndex.value);

    currentOptions.value = matchingOptions;
  } else {
    currentOptions.value = options;
  }
};

const updateOptions = debounce(async (options, searchQueryVal) => {
  if (props.async) {
    handleAsyncSearch(searchQueryVal);
  } else {
    handleSearch(searchQueryVal, options);
  }
}, 300);

const removeOption = (option) => {
  const newModelValue = props.modelValue
    .filter(({ [props.idKey]: id }) => id !== option[props.idKey]);

  emits('update:modelValue', newModelValue);
};

const selectAll = () => {
  if (allAreSelected.value) {
    emits('update:modelValue', []);
  } else {
    emits('update:modelValue', currentOptions.value);
  }
};

const handleDelete = () => {
  if (!searchQuery.value && props.modelValue.length) {
    emits('update:modelValue', props.modelValue.slice(0, -1));
  }
};

// Watchers
watch(
  [() => props.options, () => searchQuery.value],
  ([options, searchQueryVal]) => {
    updateOptions(options, searchQueryVal);
  },
  { deep: true, immediate: true },
);

onClickOutside(searchInput, () => {
  opened.value = false;
  searchQuery.value = '';
}, { ignore: [treeselectDropdown] });
</script>

<style lang="less" scoped>
@import '@/less/mixins';
.dppl-treeselect {
  overflow-y: auto;
}

.search-input {
  width: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 8px;
}

.search-row {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.actions {
  width: 100%;
}

:deep(.v-btn) {
  text-transform: none;
  width: 100%;
  height: 32px;
  letter-spacing: normal;
  max-width: 57px;

  .v-btn__content {
    font-size: 12px;
    line-height: normal;
    font-weight: 500;
  }
}

.opened {
  height: 0px;
  overflow: visible;
  position: relative;
}

.tree-view-dropdown {
  position: absolute;
  min-width: 238px;
  z-index: 2;
  background: @bg-default;
  border-radius: 6px;
  .shadow();

  &.open-on-top {
    transform: translateY(calc(-100% - 43px));
  }
}
</style>
